/**
 * Admin AI Blog tab — settings, API keys, generate, post CRUD, job history.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Sparkles, FileText, Settings, Loader2, Pencil, Eye, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getAdminBlogSettings,
  patchAdminBlogSettings,
  getAdminBlogPosts,
  getAdminBlogPost,
  patchAdminBlogPost,
  deleteAdminBlogPost,
  generateBlogArticle,
  syncBlogMemory,
  getAdminBlogJobs,
} from '../services/apiClient';

const AdminBlogTab = () => {
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeSection, setActiveSection] = useState('posts');
  const [editorPost, setEditorPost] = useState(null);
  const [editorMode, setEditorMode] = useState(null); // 'edit' | 'view'
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await getAdminBlogSettings();
      if (res?.success) setSettings(res.settings);
    } catch (e) {
      toast.error(e.message || 'Failed to load blog settings');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const res = await getAdminBlogPosts();
      if (res?.success && Array.isArray(res.posts)) setPosts(res.posts);
      else setPosts([]);
    } catch (e) {
      toast.error(e.message || 'Failed to load blog posts');
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await getAdminBlogJobs();
      if (res?.success && Array.isArray(res.jobs)) setJobs(res.jobs);
      else setJobs([]);
    } catch (e) {
      toast.error(e.message || 'Failed to load job history');
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadPosts();
    loadJobs();
  }, [loadSettings, loadPosts, loadJobs]);

  const handleSaveSettings = async (settingsUpdates, apiKeys) => {
    try {
      const res = await patchAdminBlogSettings(settingsUpdates, apiKeys);
      if (res?.success) {
        setSettings(res.settings);
        toast.success('Blog settings saved');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to save settings');
    }
  };

  const openPost = async (post, mode) => {
    setEditorLoading(true);
    setEditorMode(mode);
    try {
      const res = await getAdminBlogPost(post.id);
      if (res?.success && res.post) {
        setEditorPost(res.post);
      } else {
        setEditorPost(post);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load post');
      setEditorMode(null);
    } finally {
      setEditorLoading(false);
    }
  };

  const closeEditor = () => {
    setEditorPost(null);
    setEditorMode(null);
  };

  const handleSavePost = async (payload) => {
    if (!editorPost?.id) return;
    setEditorSaving(true);
    try {
      const res = await patchAdminBlogPost(editorPost.id, payload);
      if (res?.success) {
        toast.success('Post saved');
        closeEditor();
        loadPosts();
      }
    } catch (e) {
      toast.error(e.message || 'Failed to save post');
    } finally {
      setEditorSaving(false);
    }
  };

  const handlePublish = async (post) => {
    try {
      await patchAdminBlogPost(post.id, { status: 'published' });
      toast.success('Post published');
      loadPosts();
    } catch (e) {
      toast.error(e.message || 'Publish failed');
    }
  };

  const handleUnpublish = async (post) => {
    try {
      await patchAdminBlogPost(post.id, { status: 'draft' });
      toast.success('Post moved to drafts');
      loadPosts();
    } catch (e) {
      toast.error(e.message || 'Unpublish failed');
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await deleteAdminBlogPost(post.id);
      toast.success('Post deleted');
      if (editorPost?.id === post.id) closeEditor();
      loadPosts();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateBlogArticle();
      if (res?.success) {
        toast.success('Article generated');
        loadPosts();
        loadJobs();
      } else {
        toast.error(res?.error || 'Generation failed');
      }
    } catch (e) {
      toast.error(e.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSyncMemory = async () => {
    setSyncing(true);
    try {
      const res = await syncBlogMemory();
      if (res?.success) {
        toast.success(`Memory synced (${JSON.stringify(res.counts || {})})`);
      }
    } catch (e) {
      toast.error(e.message || 'Memory sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-theme-bronze">AI Blog</h2>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleSyncMemory} disabled={syncing} className="btn-secondary flex items-center gap-2 text-sm">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync Memory
          </button>
          <button type="button" onClick={handleGenerate} disabled={generating} className="btn-primary flex items-center gap-2 text-sm">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Generate Article
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-theme-bronze/20 pb-2">
        {[
          { id: 'posts', label: 'Posts', icon: FileText },
          { id: 'settings', label: 'Settings', icon: Settings },
          { id: 'jobs', label: 'Jobs', icon: RefreshCw },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveSection(id)}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded ${
              activeSection === id ? 'bg-theme-bronze/20 text-theme-bronze' : 'text-theme-silver hover:text-theme-bronze'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {activeSection === 'posts' && (
        <div className="glass-card-dark p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-theme-silver">All Posts</h3>
            <button type="button" onClick={loadPosts} disabled={postsLoading} className="text-theme-bronze text-sm flex items-center gap-1">
              <RefreshCw className={`h-4 w-4 ${postsLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          {postsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-theme-bronze" /></div>
          ) : posts.length === 0 ? (
            <p className="text-theme-silver/70 text-center py-8">No blog posts yet. Generate your first article.</p>
          ) : (
            <ul className="space-y-3">
              {posts.map((post) => (
                <li key={post.id} className="border border-theme-bronze/10 rounded-lg p-4">
                  <div className="flex flex-wrap justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-theme-silver">{post.title}</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          post.status === 'published'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {post.status}
                        </span>
                      </div>
                      <p className="text-xs text-theme-silver/60 mt-1">
                        /blog/{post.slug} · {post.createdAt?.slice(0, 10)}
                        {post.publishedAt && ` · published ${post.publishedAt.slice(0, 10)}`}
                      </p>
                      {post.excerpt && <p className="text-sm text-theme-silver/80 mt-2 line-clamp-2">{post.excerpt}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2 items-start">
                      <button type="button" onClick={() => openPost(post, 'view')} className="btn-secondary text-xs px-2 py-1 flex items-center gap-1">
                        <Eye className="h-3 w-3" /> View
                      </button>
                      <button type="button" onClick={() => openPost(post, 'edit')} className="btn-secondary text-xs px-2 py-1 flex items-center gap-1">
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                      {post.status === 'draft' ? (
                        <button type="button" onClick={() => handlePublish(post)} className="btn-primary text-xs px-2 py-1">
                          Publish
                        </button>
                      ) : (
                        <button type="button" onClick={() => handleUnpublish(post)} className="btn-secondary text-xs px-2 py-1">
                          Unpublish
                        </button>
                      )}
                      {post.status === 'published' && (
                        <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="text-theme-bronze text-xs underline self-center px-1">
                          Live
                        </a>
                      )}
                      <button type="button" onClick={() => handleDelete(post)} className="btn-secondary text-xs px-2 py-1 text-red-400 flex items-center gap-1">
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {activeSection === 'settings' && (
        <div className="glass-card-dark p-4">
          {settingsLoading || !settings ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-theme-bronze" /></div>
          ) : (
            <BlogSettingsForm settings={settings} onSave={handleSaveSettings} />
          )}
        </div>
      )}

      {activeSection === 'jobs' && (
        <div className="glass-card-dark p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-theme-silver">Recent Jobs</h3>
            <button type="button" onClick={loadJobs} disabled={jobsLoading} className="text-theme-bronze text-sm flex items-center gap-1">
              <RefreshCw className={`h-4 w-4 ${jobsLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          {jobsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-theme-bronze" /></div>
          ) : jobs.length === 0 ? (
            <p className="text-theme-silver/70 text-center py-8">No jobs yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {jobs.map((job) => (
                <li key={job.id || job.createdAt} className="border-b border-theme-bronze/10 pb-2">
                  <span className={`font-medium ${job.status === 'completed' ? 'text-green-400' : job.status === 'failed' ? 'text-red-400' : 'text-theme-bronze'}`}>
                    {job.status}
                  </span>
                  {' · '}{job.stage || '—'} · {job.createdAt?.slice(0, 19)}
                  {job.error && <p className="text-red-400/80 text-xs mt-1">{job.error}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {(editorMode || editorLoading) && (
        <PostEditorModal
          post={editorPost}
          mode={editorMode}
          loading={editorLoading}
          saving={editorSaving}
          onClose={closeEditor}
          onSave={handleSavePost}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

function PostEditorModal({ post, mode, loading, saving, onClose, onSave, onDelete }) {
  const [form, setForm] = useState(null);
  const readOnly = mode === 'view';

  useEffect(() => {
    if (post) {
      setForm({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        metaDescription: post.metaDescription || '',
        bodyHtml: post.bodyHtml || '',
        tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
        status: post.status || 'draft',
      });
    }
  }, [post]);

  if (!mode && !loading) return null;

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly || !form) return;
    const tags = form.tags
      ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : [];
    onSave({
      title: form.title,
      slug: form.slug,
      excerpt: form.excerpt,
      metaDescription: form.metaDescription,
      bodyHtml: form.bodyHtml,
      tags,
      status: form.status,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="glass-card-dark w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-theme-silver hover:text-theme-bronze">
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-xl font-semibold text-theme-bronze mb-4">
          {loading ? 'Loading…' : readOnly ? 'View Post' : 'Edit Post'}
        </h3>
        {loading || !form ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-theme-bronze" /></div>
        ) : readOnly ? (
          <div className="space-y-4">
            <div>
              <p className="text-xs text-theme-silver/60 uppercase">Title</p>
              <p className="text-theme-silver text-lg">{form.title}</p>
            </div>
            <div>
              <p className="text-xs text-theme-silver/60 uppercase">Slug</p>
              <p className="text-theme-silver">/blog/{form.slug}</p>
            </div>
            <div>
              <p className="text-xs text-theme-silver/60 uppercase">Status</p>
              <p className="text-theme-silver capitalize">{form.status}</p>
            </div>
            {form.excerpt && (
              <div>
                <p className="text-xs text-theme-silver/60 uppercase">Excerpt</p>
                <p className="text-theme-silver">{form.excerpt}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-theme-silver/60 uppercase mb-2">Content</p>
              <div
                className="prose prose-invert max-w-none text-theme-silver border border-theme-bronze/10 rounded p-4"
                dangerouslySetInnerHTML={{ __html: form.bodyHtml }}
              />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary">Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-theme-silver mb-1">Title</label>
              <input value={form.title} onChange={(e) => update('title', e.target.value)} className="input-field w-full" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-theme-silver mb-1">Slug</label>
                <input value={form.slug} onChange={(e) => update('slug', e.target.value)} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm text-theme-silver mb-1">Status</label>
                <select value={form.status} onChange={(e) => update('status', e.target.value)} className="input-field w-full">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-theme-silver mb-1">Excerpt</label>
              <textarea value={form.excerpt} onChange={(e) => update('excerpt', e.target.value)} className="input-field w-full h-16" />
            </div>
            <div>
              <label className="block text-sm text-theme-silver mb-1">Meta description (SEO)</label>
              <textarea value={form.metaDescription} onChange={(e) => update('metaDescription', e.target.value)} className="input-field w-full h-16" />
            </div>
            <div>
              <label className="block text-sm text-theme-silver mb-1">Tags (comma-separated)</label>
              <input value={form.tags} onChange={(e) => update('tags', e.target.value)} className="input-field w-full" />
            </div>
            <div>
              <label className="block text-sm text-theme-silver mb-1">Body (HTML)</label>
              <textarea value={form.bodyHtml} onChange={(e) => update('bodyHtml', e.target.value)} className="input-field w-full h-64 font-mono text-sm" />
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </button>
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              {post && (
                <button type="button" onClick={() => onDelete(post)} className="btn-secondary text-red-400 ml-auto">
                  Delete Post
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function BlogSettingsForm({ settings, onSave }) {
  const [form, setForm] = useState({ ...settings });
  const [apiKeys, setApiKeys] = useState({ openrouter: '', openai: '', anthropic: '' });
  const [clearKeys, setClearKeys] = useState({ openrouter: false, openai: false, anthropic: false });

  useEffect(() => {
    setForm({
      ...settings,
      provider_models: {
        openrouter: settings.provider_models?.openrouter || '',
        openai: settings.provider_models?.openai || '',
        anthropic: settings.provider_models?.anthropic || '',
      },
    });
    setApiKeys({ openrouter: '', openai: '', anthropic: '' });
    setClearKeys({ openrouter: false, openai: false, anthropic: false });
  }, [settings]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const providerModels = form.provider_models || {
    openrouter: '',
    openai: '',
    anthropic: '',
  };

  const updateProviderModel = (provider, value) => {
    setForm((f) => ({
      ...f,
      provider_models: {
        ...(f.provider_models || { openrouter: '', openai: '', anthropic: '' }),
        [provider]: value,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { keys, ...rest } = form;
    const keyPayload = {};
    ['openrouter', 'openai', 'anthropic'].forEach((name) => {
      if (clearKeys[name]) {
        keyPayload[name] = '';
      } else if (apiKeys[name]?.trim()) {
        keyPayload[name] = apiKeys[name].trim();
      }
    });
    const hasKeyUpdates = Object.keys(keyPayload).length > 0;
    onSave(rest, hasKeyUpdates ? keyPayload : null);
    setApiKeys({ openrouter: '', openai: '', anthropic: '' });
  };

  const KeyField = ({ name, label }) => (
    <div>
      <label className="block text-sm text-theme-silver mb-1">{label}</label>
      <input
        type="password"
        autoComplete="off"
        placeholder={settings.keys?.[name] ? '••••••••  (configured — enter new key to replace)' : 'Paste API key'}
        value={apiKeys[name]}
        onChange={(e) => {
          setApiKeys((k) => ({ ...k, [name]: e.target.value }));
          if (e.target.value) setClearKeys((c) => ({ ...c, [name]: false }));
        }}
        className="input-field w-full"
        disabled={clearKeys[name]}
      />
      <label className="flex items-center gap-2 text-xs text-theme-silver/60 mt-1">
        <input
          type="checkbox"
          checked={clearKeys[name]}
          onChange={(e) => setClearKeys((c) => ({ ...c, [name]: e.target.checked }))}
        />
        Remove saved key
        {settings.keys?.[name] && !clearKeys[name] && (
          <span className="text-green-400 ml-1">Configured</span>
        )}
      </label>
    </div>
  );

  return (
    <form className="space-y-6 max-w-xl" onSubmit={handleSubmit}>
      <div>
        <h4 className="text-sm font-semibold text-theme-bronze mb-3">API Keys</h4>
        <p className="text-xs text-theme-silver/60 mb-4">
          Keys are encrypted in the database and never shown after saving. Leave blank to keep existing keys.
          Secrets Manager env vars are used as fallback if no admin key is saved.
        </p>
        <div className="space-y-4">
          <KeyField name="openrouter" label="OpenRouter API key" />
          <KeyField name="openai" label="OpenAI API key" />
          <KeyField name="anthropic" label="Anthropic API key" />
        </div>
      </div>

      <div className="border-t border-theme-bronze/20 pt-4 space-y-4">
        <h4 className="text-sm font-semibold text-theme-bronze">Site context for AI memory</h4>
        <p className="text-xs text-theme-silver/60">
          Teach the AI about your business, audience, products, competitors, and keywords. Use any
          format — section headers, nested labels, or bullet lists (e.g.{' '}
          <code className="text-theme-silver/70">SEO priorities:</code> then{' '}
          <code className="text-theme-silver/70">High-intent keywords:</code>, or{' '}
          <code className="text-theme-silver/70">Competitors:</code> with{' '}
          <code className="text-theme-silver/70">Primary:</code> sub-lines). Saved here and injected
          into memory on <strong className="text-theme-silver/80">Sync Memory</strong>; parsed for
          topic planning, competitor/keyword web research, and writing.
        </p>
        <div>
          <label className="block text-sm text-theme-silver mb-1">Manual site context</label>
          <textarea
            value={form.manual_memory_context || ''}
            onChange={(e) => update('manual_memory_context', e.target.value)}
            className="input-field w-full h-48 font-mono text-sm"
            placeholder={
              'Example:\n'
              + '- Tolbert Tactical sells premium tactical gear and runs firearm giveaways in Utah.\n'
              + '- Target audience: Utah gun owners, LEO, military, outdoor enthusiasts.\n'
              + '- Competitors: [list sites/brands to differentiate from]\n'
              + '- Priority topics/keywords: concealed carry, range gear, EDC, Utah gun laws (informational only)\n'
              + '- Tone: expert but approachable; never give legal/medical advice.'
            }
          />
          <p className="text-xs text-theme-silver/50 mt-1">
            {(form.manual_memory_context || '').length.toLocaleString()} / 50,000 characters
          </p>
        </div>
      </div>

      <div className="border-t border-theme-bronze/20 pt-4 space-y-4">
        <h4 className="text-sm font-semibold text-theme-bronze">Generation</h4>
        <div>
          <label className="block text-sm text-theme-silver mb-1">AI Provider</label>
          <select value={form.provider || 'openrouter'} onChange={(e) => update('provider', e.target.value)} className="input-field w-full">
            <option value="openrouter">OpenRouter (default)</option>
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic</option>
          </select>
        </div>
        <div className="space-y-3">
          <label className="block text-sm text-theme-silver">Model ID per provider (optional)</label>
          <p className="text-xs text-theme-silver/50">
            Each provider keeps its own model. The active provider above is used for generation.
            Leave blank to use defaults (OpenRouter auto-selects a free model).
          </p>
          <div>
            <label className="block text-xs text-theme-silver/70 mb-1">OpenRouter model</label>
            <input
              value={providerModels.openrouter || ''}
              onChange={(e) => updateProviderModel('openrouter', e.target.value)}
              className="input-field w-full font-mono text-sm"
              placeholder="Blank = auto free model; e.g. google/gemini-2.5-flash-preview"
            />
          </div>
          <div>
            <label className="block text-xs text-theme-silver/70 mb-1">OpenAI model</label>
            <input
              value={providerModels.openai || ''}
              onChange={(e) => updateProviderModel('openai', e.target.value)}
              className="input-field w-full font-mono text-sm"
              placeholder="Blank = gpt-4o-mini"
            />
          </div>
          <div>
            <label className="block text-xs text-theme-silver/70 mb-1">Anthropic model</label>
            <input
              value={providerModels.anthropic || ''}
              onChange={(e) => updateProviderModel('anthropic', e.target.value)}
              className="input-field w-full font-mono text-sm"
              placeholder="Blank = claude-3-5-haiku-20241022"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm text-theme-silver mb-1">Publish mode (auto-generate)</label>
          <select value={form.publish_mode || 'draft'} onChange={(e) => update('publish_mode', e.target.value)} className="input-field w-full">
            <option value="draft">Draft (review before publish)</option>
            <option value="published">Published immediately</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-theme-silver mb-1">Frequency</label>
          <select value={form.frequency || 'weekly'} onChange={(e) => update('frequency', e.target.value)} className="input-field w-full">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-theme-silver">
          <input type="checkbox" checked={!!form.auto_post_enabled} onChange={(e) => update('auto_post_enabled', e.target.checked)} />
          Enable scheduled auto-generation
        </label>
        <label className="flex items-center gap-2 text-sm text-theme-silver">
          <input type="checkbox" checked={form.block_ymyl !== false} onChange={(e) => update('block_ymyl', e.target.checked)} />
          Block YMYL topics (recommended)
        </label>
        <div>
          <label className="block text-sm text-theme-silver mb-1">Brand voice</label>
          <textarea value={form.brand_voice || ''} onChange={(e) => update('brand_voice', e.target.value)} className="input-field w-full h-20" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-theme-silver mb-1">City</label>
            <input value={form.local_city || ''} onChange={(e) => update('local_city', e.target.value)} className="input-field w-full" />
          </div>
          <div>
            <label className="block text-sm text-theme-silver mb-1">State</label>
            <input value={form.local_state || ''} onChange={(e) => update('local_state', e.target.value)} className="input-field w-full" />
          </div>
        </div>
      </div>
      <button type="submit" className="btn-primary">Save Settings</button>
    </form>
  );
}

export default AdminBlogTab;
